import { FC } from 'react';

type AlertVariant = 'success' | 'error' | 'warning' | 'info';

interface AlertProps {
  variant: AlertVariant;
  message: string;
  className?: string;
  onDismiss?: () => void;
}

const variantStyles = {
  success: {
    bg: 'bg-white',
    border: 'border-green-500',
    text: 'text-gray-900',
    iconColor: 'text-green-500',
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    ),
  },
  error: {
    bg: 'bg-white',
    border: 'border-red-500',
    text: 'text-gray-900',
    iconColor: 'text-red-500',
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    ),
  },
  warning: {
    bg: 'bg-white',
    border: 'border-yellow-500',
    text: 'text-gray-900',
    iconColor: 'text-yellow-600',
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
  },
  info: {
    bg: 'bg-white',
    border: 'border-blue-500',
    text: 'text-gray-900',
    iconColor: 'text-blue-500',
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    ),
  },
};

const Alert: FC<AlertProps> = ({
  variant = 'info',
  message,
  className = '',
  onDismiss,
}) => {
  const { bg, border, text, iconColor, icon } = variantStyles[variant];

  return (
    <div
      className={`${bg} border-l-4 ${border} ${text} px-4 py-3 rounded-r-lg shadow-lg ${className}`}
      role="alert"
    >
      <div className="flex items-center gap-3">
        <span className={`flex-shrink-0 ${iconColor}`}>{icon}</span>
        <span className="flex-1 text-sm font-medium">{message}</span>
        {onDismiss && (
          <button
            type="button"
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none bg-transparent border-0 cursor-pointer p-0"
            onClick={onDismiss}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default Alert;
